/**
 * CancellationRulesEditor — aba "Cancelamento" do painel de configuração de
 * node (spec 2026-09-14 §1.1). Disponível em TODO node (gatilho incluído):
 * "vale para o fluxo inteiro" no gatilho, "vale deste nó em diante" nos
 * demais.
 *
 * Sem `useEffect` gravando padrão na montagem — a mesma decisão do Aguardar
 * antigo (`WaitForConfig.tsx`): nenhum evento marcado = a chave `cancellation`
 * fica AUSENTE no node (não `{ rules: [] }`), e o mapeador de
 * `useWorkflowBuilder.ts` só grava a chave quando ela existe.
 *
 * Campos um abaixo do outro (nunca lado a lado) — padrão do formulário de
 * canal.
 */
import type { WorkflowCancellationEventType, WorkflowCancellationRule, WorkflowEventActor, WorkflowNodeCancellation } from '@troia-v3/types';
import { WORKFLOW_CANCELLATION_EVENTS } from '@troia-v3/types';

import { MultiSelect } from '@/shared/components/ui/MultiSelect';

import { VariableInput } from '../VariableInput';
import type { AvailableContext } from '../../utils/node-schemas';
import { ACTOR_LABELS, CANCELLATION_EVENT_GROUPS, CANCELLATION_EVENT_LABELS, defaultTargetLabel } from '../../utils/cancellation-events';

interface CancellationRulesEditorProps {
  value: WorkflowNodeCancellation | undefined;
  onChange: (next: WorkflowNodeCancellation | undefined) => void;
  availableContext: AvailableContext;
  isTrigger: boolean;
  /**
   * É o nó Aguardar? Só ele ganha a saída "Cancelado" por ter regra própria
   * (spec 2026-09-20 §7.2, D12) — e só nele a dica dos dois resultados faz
   * sentido.
   */
  isWaitNode: boolean;
}

const EVENT_OPTIONS = CANCELLATION_EVENT_GROUPS.flatMap((g) =>
  g.events.map((e) => ({ value: e, label: `${g.label}: ${CANCELLATION_EVENT_LABELS[e]}` }))
);

export function CancellationRulesEditor({ value, onChange, availableContext, isTrigger, isWaitNode }: CancellationRulesEditorProps) {
  const rules = value?.rules ?? [];
  const selected = rules.map((r) => r.eventType);

  const emit = (next: WorkflowCancellationRule[]) => onChange(next.length > 0 ? { rules: next } : undefined);

  const changeEvents = (values: string[]) => {
    const kept = rules.filter((r) => values.includes(r.eventType));
    const added = values
      .filter((v) => !selected.includes(v as WorkflowCancellationEventType))
      .map((v) => ({ eventType: v as WorkflowCancellationEventType }));
    emit([...kept, ...added]);
  };

  const patch = (eventType: WorkflowCancellationEventType, change: Partial<WorkflowCancellationRule>) =>
    emit(
      rules.map((r) => {
        if (r.eventType !== eventType) return r;
        const next: WorkflowCancellationRule = { eventType: r.eventType };
        const actors = change.actors ?? r.actors;
        const refersTo = 'refersTo' in change ? change.refersTo : r.refersTo;
        if (actors !== undefined && actors.length > 0) next.actors = actors;
        if (refersTo !== undefined && refersTo !== '') next.refersTo = refersTo;
        return next;
      })
    );

  return (
    <div className="space-y-4">
      {/* Ressalva da D15 (spec 2026-09-20 §7.2): vale em TODO nó, gatilho
          incluído — quem desvia é o Aguardar, mas quem deixa de valer são as
          regras de QUALQUER nó, do mesmo tipo do evento que desviou. */}
      <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }} data-testid="cancel-rules-scope-note">
        {isTrigger ? 'Vale para o fluxo inteiro.' : 'Vale deste nó em diante.'} Basta um acontecer para cancelar a execução
        {' — exceto: se um Aguardar desviar a execução pela saída Cancelado, as regras '}
        <strong>do mesmo tipo do evento que desviou</strong>
        {' deixam de valer dali em diante. As demais continuam.'}
      </p>

      {isWaitNode && (
        <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }} data-testid="cancel-rules-wait-note">
          {rules.length === 0 ? (
            <>
              Adicione ao menos uma regra aqui para este Aguardar ganhar a saída Cancelado. Com ela ligada,{' '}
              <strong>qualquer</strong> regra de cancelamento da execução — inclusive a do gatilho — faz o fluxo seguir
              por ali em vez de ser cancelado.
            </>
          ) : (
            <>
              Este Aguardar tem a saída Cancelado. Com nada ligado nela, a execução é cancelada, como em qualquer outro
              nó. Com algum nó ligado, o fluxo segue por ali — seja qual for a regra de cancelamento que disparou,
              inclusive a do gatilho.
            </>
          )}
        </p>
      )}

      <div>
        <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--theme-muted-text)' }}>
          Cancelar se acontecer
        </label>
        <MultiSelect
          options={EVENT_OPTIONS}
          selectedValues={selected}
          onChange={changeEvents}
          placeholder="Escolha um ou mais eventos"
          data-testid="cancel-rules-events"
        />
      </div>

      {rules.map((rule) => {
        const spec = WORKFLOW_CANCELLATION_EVENTS[rule.eventType];
        // M1 (revisão final): evento fora do catálogo hoje (dado de
        // import/IA/futuro) não pode derrubar o painel — sem `spec` não há
        // como saber "Quem fez", então o bloco desse evento simplesmente não
        // é desenhado (o evento continua marcado em `selected`/MultiSelect).
        if (!spec) return null;
        return (
          <div key={rule.eventType} className="space-y-3 rounded border p-3" style={{ borderColor: 'var(--theme-card-border)' }}>
            <p className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>
              {CANCELLATION_EVENT_LABELS[rule.eventType]}
            </p>

            {spec.actors.length > 0 && (
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--theme-muted-text)' }}>
                  Quem fez
                </label>
                <MultiSelect
                  options={spec.actors.map((a) => ({ value: a, label: ACTOR_LABELS[a as WorkflowEventActor] }))}
                  selectedValues={rule.actors ?? []}
                  onChange={(v) => patch(rule.eventType, { actors: v as WorkflowEventActor[] })}
                  placeholder="Qualquer origem"
                  data-testid={`cancel-rule-actors-${rule.eventType}`}
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--theme-muted-text)' }}>
                A quem se refere
              </label>
              <VariableInput
                value={rule.refersTo ?? ''}
                onChange={(v) => patch(rule.eventType, { refersTo: v })}
                availableContext={availableContext}
                placeholder={`Em branco: ${defaultTargetLabel(rule.eventType)} deste fluxo`}
                data-testid={`cancel-rule-refers-to-${rule.eventType}`}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default CancellationRulesEditor;
